import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Snowflake, Thermometer, Clock, MapPin, Loader2, AlertTriangle, CheckCircle, Settings } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export function DecongelationPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({ emplacements: [], types_produit: [] });
  const [emplacements, setEmplacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterStatut, setFilterStatut] = useState('all');
  const [editEmpl, setEditEmpl] = useState(null);
  const [newEmpl, setNewEmpl] = useState({ nom: '', type: 'frigo_positif', description: '' });
  const [newType, setNewType] = useState('');

  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const nowTime = now.toTimeString().slice(0, 5);

  const [form, setForm] = useState({
    produit: '', type_produit: '', numero_lot: '',
    date_decongelation: todayStr, heure_decongelation: nowTime,
    emplacement: '', quantite: '', dlc_apres_decongelation: '', notes: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatut !== 'all' ? { statut: filterStatut } : {};
      const [iRes, cRes, eRes] = await Promise.all([
        api.get('/decongelation', { params }),
        api.get('/decongelation/config'),
        api.get('/decongelation/emplacements'),
      ]);
      setItems(iRes.data);
      setConfig(cRes.data);
      setEmplacements(eRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api, filterStatut]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({
      produit: '', type_produit: '', numero_lot: '',
      date_decongelation: todayStr, heure_decongelation: nowTime,
      emplacement: '', quantite: '', dlc_apres_decongelation: '', notes: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      produit: item.produit, type_produit: item.type_produit, numero_lot: item.numero_lot,
      date_decongelation: item.date_decongelation, heure_decongelation: item.heure_decongelation,
      emplacement: item.emplacement, quantite: item.quantite || '',
      dlc_apres_decongelation: item.dlc_apres_decongelation || '', notes: item.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.produit.trim() || !form.numero_lot.trim()) {
      toast.error('Produit et numero de lot requis'); return;
    }
    try {
      if (editItem) {
        await api.put(`/decongelation/${editItem.id}`, form);
        toast.success('Mis a jour');
      } else {
        await api.post('/decongelation', form);
        toast.success('Decongelation enregistree');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/decongelation/${id}`); toast.success('Supprime'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const handleStatut = async (id, statut) => {
    try {
      await api.put(`/decongelation/${id}`, { statut });
      toast.success(statut === 'utilise' ? 'Marque comme utilise' : 'Statut mis a jour');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const isExpired = (dlc) => {
    if (!dlc) return false;
    return dlc < todayStr;
  };

  const enCours = items.filter(i => i.statut === 'en_cours');
  const utilises = items.filter(i => i.statut === 'utilise');
  const expires = enCours.filter(i => isExpired(i.dlc_apres_decongelation));

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="decongelation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Snowflake className="w-6 h-6 text-blue-500" /> Suivi Decongelation
          </h1>
          <p className="text-muted-foreground text-sm">Tracabilite des produits decongeles — lot, date, heure, emplacement</p>
        </div>
        <Button onClick={openCreate} data-testid="add-decongelation-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle decongelation
        </Button>
      </div>

      <Tabs defaultValue="suivi">
        <TabsList>
          <TabsTrigger value="suivi" data-testid="tab-suivi"><Snowflake className="w-4 h-4 mr-1" /> Suivi</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config-decong"><Settings className="w-4 h-4 mr-1" /> Frigos & Types</TabsTrigger>
        </TabsList>

        <TabsContent value="suivi" className="space-y-4">
      {/* Alerts */}
      {expires.length > 0 && (
        <Card className="border-2 border-red-300 bg-red-50/50" data-testid="dlc-alert">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-700">{expires.length} produit(s) avec DLC depassee !</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {expires.map(e => (
                    <Badge key={e.id} className="bg-red-100 text-red-700">{e.produit} (lot {e.numero_lot}) — DLC {e.dlc_apres_decongelation}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-blue-200">
          <CardContent className="pt-5 pb-4 text-center">
            <Snowflake className="w-6 h-6 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold text-blue-600">{enCours.length}</p>
            <p className="text-xs text-muted-foreground">En decongelation</p>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="pt-5 pb-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold text-green-600">{utilises.length}</p>
            <p className="text-xs text-muted-foreground">Utilises</p>
          </CardContent>
        </Card>
        <Card className={expires.length > 0 ? 'border-red-300 bg-red-50/30' : 'border-muted'}>
          <CardContent className="pt-5 pb-4 text-center">
            <AlertTriangle className={`w-6 h-6 mx-auto mb-1 ${expires.length > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
            <p className={`text-2xl font-bold ${expires.length > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>{expires.length}</p>
            <p className="text-xs text-muted-foreground">DLC depassee</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-[180px]" data-testid="filter-statut"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="en_cours">En decongelation</SelectItem>
            <SelectItem value="utilise">Utilises</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Snowflake className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Aucune decongelation</h3>
          <p className="text-muted-foreground mb-4">Enregistrez vos decongelations pour la tracabilite HACCP</p>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Enregistrer une decongelation</Button>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Statut</TableHead>
                <TableHead>Produit</TableHead>
                <TableHead>N° Lot</TableHead>
                <TableHead>Date / Heure</TableHead>
                <TableHead>Emplacement</TableHead>
                <TableHead>Quantite</TableHead>
                <TableHead>DLC apres decong.</TableHead>
                <TableHead>Par</TableHead>
                <TableHead className="w-[120px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(item => {
                const expired = isExpired(item.dlc_apres_decongelation);
                return (
                  <TableRow key={item.id} className={expired && item.statut === 'en_cours' ? 'bg-red-50/50' : ''} data-testid={`decong-row-${item.id}`}>
                    <TableCell>
                      {item.statut === 'en_cours'
                        ? <Badge className="bg-blue-100 text-blue-700"><Snowflake className="w-3 h-3 mr-1" /> En cours</Badge>
                        : <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Utilise</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold">{item.produit}</div>
                      {item.type_produit && <div className="text-xs text-muted-foreground">{item.type_produit}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{item.numero_lot}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        {item.date_decongelation} a {item.heure_decongelation}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3 text-muted-foreground" />
                        {item.emplacement || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{item.quantite || '-'}</TableCell>
                    <TableCell>
                      {item.dlc_apres_decongelation ? (
                        <Badge className={expired ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-700'}>
                          {expired && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {item.dlc_apres_decongelation}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{item.utilisateur}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {item.statut === 'en_cours' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300" onClick={() => handleStatut(item.id, 'utilise')}
                            data-testid={`use-${item.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Utilise
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(item.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
        </TabsContent>

        {/* CONFIG TAB */}
        <TabsContent value="config" className="space-y-6">
          {/* Emplacements / Frigos */}
          <Card data-testid="config-emplacements">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-5 h-5" /> Emplacements (Frigos)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emplacements.map(e => (
                    <TableRow key={e.id} data-testid={`empl-row-${e.id}`}>
                      {editEmpl?.id === e.id ? (
                        <>
                          <TableCell><Input value={editEmpl.nom} onChange={ev => setEditEmpl(p => ({...p, nom: ev.target.value}))} className="h-8" /></TableCell>
                          <TableCell>
                            <Select value={editEmpl.type || 'frigo_positif'} onValueChange={v => setEditEmpl(p => ({...p, type: v}))}>
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="frigo_positif">Frigo positif</SelectItem>
                                <SelectItem value="saladette">Saladette</SelectItem>
                                <SelectItem value="congelateur">Congelateur</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell><Input value={editEmpl.description} onChange={ev => setEditEmpl(p => ({...p, description: ev.target.value}))} className="h-8" placeholder="Description..." /></TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7" onClick={async () => {
                                try { await api.put(`/decongelation/emplacements/${e.id}`, editEmpl); toast.success('Mis a jour'); setEditEmpl(null); fetchData(); }
                                catch { toast.error('Erreur'); }
                              }}>OK</Button>
                              <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditEmpl(null)}>X</Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-medium">{e.nom}</TableCell>
                          <TableCell>
                            {e.type === 'congelateur' ? <Badge className="bg-blue-100 text-blue-700">Congélateur</Badge> :
                             e.type === 'saladette' ? <Badge className="bg-green-100 text-green-700">Saladette</Badge> :
                             <Badge className="bg-amber-100 text-amber-700">Frigo positif</Badge>}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{e.description || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditEmpl({...e})}><Pencil className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => {
                                if (!window.confirm(`Supprimer le frigo "${e.nom}" ? Cette action est irreversible.`)) return;
                                try { await api.delete(`/decongelation/emplacements/${e.id}`); toast.success('Supprime'); fetchData(); }
                                catch { toast.error('Erreur'); }
                              }}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-end gap-2 pt-2 border-t flex-wrap">
                <div className="flex-1 min-w-[140px] space-y-1">
                  <Label className="text-xs">Nom</Label>
                  <Input value={newEmpl.nom} onChange={e => setNewEmpl(p => ({...p, nom: e.target.value}))} placeholder="Ex: Frigo Positif 3" data-testid="new-empl-nom" />
                </div>
                <div className="w-40 space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={newEmpl.type || 'frigo_positif'} onValueChange={v => setNewEmpl(p => ({...p, type: v}))}>
                    <SelectTrigger data-testid="new-empl-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="frigo_positif">Frigo positif</SelectItem>
                      <SelectItem value="saladette">Saladette</SelectItem>
                      <SelectItem value="congelateur">Congelateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[140px] space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input value={newEmpl.description} onChange={e => setNewEmpl(p => ({...p, description: e.target.value}))} placeholder="Ex: Reserve aux poissons" data-testid="new-empl-desc" />
                </div>
                <Button onClick={async () => {
                  if (!newEmpl.nom.trim()) { toast.error('Nom requis'); return; }
                  try { await api.post('/decongelation/emplacements', newEmpl); toast.success('Emplacement ajoute'); setNewEmpl({ nom: '', type: 'frigo_positif', description: '' }); fetchData(); }
                  catch { toast.error('Erreur'); }
                }} data-testid="add-empl-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
              </div>
            </CardContent>
          </Card>

          {/* Types de produit */}
          <Card data-testid="config-types">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Thermometer className="w-5 h-5" /> Types de produit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {config.types_produit.map((t, i) => (
                  <div key={i} className="flex items-center gap-1 px-3 py-1.5 bg-muted/50 rounded-full border">
                    <span className="text-sm">{t}</span>
                    <button onClick={async () => {
                      const types = await api.get('/decongelation/types');
                      const item = types.data.find(x => x.nom === t);
                      if (item) {
                        try { await api.delete(`/decongelation/types/${item.id}`); toast.success('Supprime'); fetchData(); }
                        catch { toast.error('Erreur'); }
                      }
                    }} className="ml-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2 pt-2 border-t">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Nouveau type</Label>
                  <Input value={newType} onChange={e => setNewType(e.target.value)} placeholder="Ex: Gibier" data-testid="new-type" />
                </div>
                <Button onClick={async () => {
                  if (!newType.trim()) { toast.error('Nom requis'); return; }
                  try { await api.post('/decongelation/types', { nom: newType }); toast.success('Type ajoute'); setNewType(''); fetchData(); }
                  catch { toast.error('Erreur'); }
                }} data-testid="add-type-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="decongelation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-blue-500" />
              {editItem ? 'Modifier' : 'Nouvelle'} decongelation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Produit *</Label>
                <Input value={form.produit} onChange={e => setForm(f => ({...f, produit: e.target.value}))}
                  placeholder="Ex: Filet de saumon" data-testid="decong-produit" />
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={form.type_produit || 'Autre'} onValueChange={v => setForm(f => ({...f, type_produit: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {config.types_produit.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Numero de lot *</Label>
              <Input value={form.numero_lot} onChange={e => setForm(f => ({...f, numero_lot: e.target.value}))}
                placeholder="Ex: LOT-2026-04-001" className="font-mono" data-testid="decong-lot" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date de decongelation</Label>
                <Input type="date" value={form.date_decongelation} onChange={e => setForm(f => ({...f, date_decongelation: e.target.value}))} data-testid="decong-date" />
              </div>
              <div className="space-y-1">
                <Label>Heure</Label>
                <Input type="time" value={form.heure_decongelation} onChange={e => setForm(f => ({...f, heure_decongelation: e.target.value}))} data-testid="decong-heure" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Emplacement frigo</Label>
                <Select value={form.emplacement || ''} onValueChange={v => setForm(f => ({...f, emplacement: v}))}>
                  <SelectTrigger data-testid="decong-emplacement"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {emplacements.map(e => (
                      <SelectItem key={e.id} value={e.nom}>
                        {e.nom}{e.description ? ` — ${e.description}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Quantite</Label>
                <Input value={form.quantite} onChange={e => setForm(f => ({...f, quantite: e.target.value}))}
                  placeholder="Ex: 2 kg, 5 filets" data-testid="decong-quantite" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>DLC apres decongelation</Label>
                <Input type="date" value={form.dlc_apres_decongelation} onChange={e => setForm(f => ({...f, dlc_apres_decongelation: e.target.value}))} data-testid="decong-dlc" />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Ex: Pour service midi" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSave} data-testid="save-decongelation-btn">{editItem ? 'Enregistrer' : 'Valider'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
